import { useQuery } from '@apollo/client';
import type { PortfolioReport } from '@interfaces/portfolioReport';
import { useMemo } from 'react';
import { FETCH_OPTIMIZED_PORTFOLIO_REPORTS } from '../queries/optimizedReports.queries';
import { FETCH_PORTFOLIO_REPORTS_BY_OPTIMIZER_RUN } from '../queries/optimizerRunReports.queries';
import type { OptimizerAuditV2Record } from '../types/audit.types';
import type { BulkRebalanceRun } from '../types/run.types';
import type { RunReportPortfolioFilters } from '../types/runTable.types';
import { OPTIMIZER_POLL_INTERVAL_MS } from '../utils/detailPage/constants';
import {
  mergeAllRunReports,
  resolveReportsTotalCount,
  sumPendingTradeRequestCount,
} from '../utils/detailPage/detailPagePresentation';
import {
  resolveReportsFetchPageSize,
  resolveReportsFullyLoaded,
  resolveReportsPaginationOffset,
  resolveStreamingReportsPageSize,
} from '../utils/detailPage/reportsFetchSizing';
import { getMissingAuditPortfolioReportIds } from '../utils/reports/getMissingAuditPortfolioReportIds';
import { mergeRunPortfolioReports } from '../utils/reports/mergeRunPortfolioReports';
import { useFailedPortfolioReportLookup } from './useFailedPortfolioReportLookup';

export type UseBulkRebalanceDetailReportsParams = {
  runId: string | undefined;
  run: BulkRebalanceRun | null;
  showReports: boolean;
  canReadReports: boolean;
  useOptimizedReportsQuery: boolean;
  portfolioFilters: RunReportPortfolioFilters;
  reportsPage: number;
  inProgress: boolean;
  organizationId: string | undefined;
  auditRecords: OptimizerAuditV2Record[];
};

export type UseBulkRebalanceDetailReportsResult = {
  allRunReports: PortfolioReport[];
  reportsQueryLoading: boolean;
  reportsLoading: boolean;
  reportsTotalCount: number;
  reportsFullyLoaded: boolean;
  failedPortfolioReportsLoading: boolean;
  pendingTradeRequestCount: number;
};

export const useBulkRebalanceDetailReports = ({
  runId,
  run,
  showReports,
  canReadReports,
  useOptimizedReportsQuery,
  portfolioFilters,
  reportsPage,
  inProgress,
  organizationId,
  auditRecords,
}: UseBulkRebalanceDetailReportsParams): UseBulkRebalanceDetailReportsResult => {
  const succeeded = run?.portfolioSummary?.succeeded ?? 0;
  const runTotal = run?.portfolioSummary?.total ?? 0;

  const reportsFetchPageSize = useMemo(
    () => resolveReportsFetchPageSize(succeeded, portfolioFilters),
    [portfolioFilters, succeeded],
  );

  const streamingReportsPageSize = useMemo(
    () => resolveStreamingReportsPageSize(runTotal),
    [runTotal],
  );

  const reportsFullyLoaded = useMemo(
    () => resolveReportsFullyLoaded(runTotal, streamingReportsPageSize),
    [runTotal, streamingReportsPageSize],
  );

  const { loading: optimizedReportsLoading, data: optimizedReportsData } =
    useQuery(FETCH_OPTIMIZED_PORTFOLIO_REPORTS, {
      skip:
        !runId || !showReports || !canReadReports || !useOptimizedReportsQuery,
      fetchPolicy: 'no-cache',
      variables: {
        input: {
          organizationId,
          portfolioOptimizerId: runId,
          pagination: {
            perPage: reportsFetchPageSize,
            offSet: resolveReportsPaginationOffset(
              reportsFetchPageSize,
              succeeded,
              reportsPage,
            ),
          },
        },
      },
    });

  const { loading: streamingReportsLoading, data: streamingReportsData } =
    useQuery(FETCH_PORTFOLIO_REPORTS_BY_OPTIMIZER_RUN, {
      skip: !runId || !showReports || !canReadReports,
      fetchPolicy: 'no-cache',
      pollInterval: inProgress ? OPTIMIZER_POLL_INTERVAL_MS : 0,
      variables: {
        input: {
          filter: {
            organizationId,
            optimizerRunId: runId,
          },
          pagination: {
            perPage: streamingReportsPageSize,
            offSet: 0,
          },
        },
      },
    });

  const reports = useMemo(() => {
    const optimizerRunReports =
      streamingReportsData?.fetchPortfolioReports?.portfolioReports ?? [];
    const optimizedReports =
      optimizedReportsData?.fetchOptimizedPortfolioReports?.portfolioReports ??
      [];

    if (useOptimizedReportsQuery) {
      return mergeRunPortfolioReports(optimizerRunReports, optimizedReports);
    }

    return optimizerRunReports;
  }, [optimizedReportsData, streamingReportsData, useOptimizedReportsQuery]);

  const optimizedReportsTotalCount =
    optimizedReportsData?.fetchOptimizedPortfolioReports?.totalCount ?? 0;
  const streamingReportsTotalCount =
    streamingReportsData?.fetchPortfolioReports?.totalCount ?? 0;
  const reportsQueryLoading =
    streamingReportsLoading &&
    !streamingReportsData &&
    (!useOptimizedReportsQuery ||
      (optimizedReportsLoading && !optimizedReportsData));

  const missingReportIds = useMemo(() => {
    const reportsById = new Map<string, PortfolioReport>(
      reports.map((report: PortfolioReport) => [report.id, report]),
    );

    return getMissingAuditPortfolioReportIds(auditRecords, reportsById);
  }, [auditRecords, reports]);

  const { failedReports, loading: failedPortfolioReportsLoading } =
    useFailedPortfolioReportLookup(organizationId, missingReportIds, {
      optimizerRunId: runId,
    });

  const allRunReports = useMemo(
    () => mergeAllRunReports(reports, failedReports),
    [failedReports, reports],
  );

  const reportsTotalCount = resolveReportsTotalCount({
    auditRecordsLength: auditRecords.length,
    runTotal,
    streamingReportsTotalCount,
    optimizedReportsTotalCount,
    useOptimizedReportsQuery,
    allRunReportsLength: allRunReports.length,
  });

  const pendingTradeRequestCount = useMemo(
    () => sumPendingTradeRequestCount(reports),
    [reports],
  );

  return {
    allRunReports,
    reportsQueryLoading,
    reportsLoading: reportsQueryLoading && allRunReports.length === 0,
    reportsTotalCount,
    reportsFullyLoaded,
    failedPortfolioReportsLoading,
    pendingTradeRequestCount,
  };
};
